// Widget Component Registry
import React from 'react';
import widgetService from '../services/widgetService';
import TextLabel from './TextLabel';
import SimpleTextLabel from './SimpleTextLabel';
import TextBox from './TextBox';
import Button from './Button';
import LEDIndicator from './LEDIndicator';
import ProgressBar from './ProgressBar';
import Gauge from './Gauge';
import Image from './Image';
import ToggleSwitch from './ToggleSwitch';
import TrendChart from './TrendChart';
import ComboBox from './ComboBox';
import ButtonMenu from './ButtonMenu';
import SetValue from './SetValue';
import SetValueInput from './SetValueInput';
import Box from './Box';
import Line from './Line';
import Circle from './Circle';
import LEDLabel from './LEDLabel';
import PopupWidget from './PopupWidget';
import VRFRoomWidget from './VRFRoomWidget';
import VRFFloorControl from './VRFFloorControl';

// ✨ Get company ID from session/local storage
const getCompanyId = () => {
  // Get from sessionStorage (set by TesisSec) - stored as JSON string
  const sessionCompanyId = sessionStorage.getItem('TEMPCOM');

  if (sessionCompanyId) {
    try {
      const parsed = parseInt(JSON.parse(sessionCompanyId));
      return parsed;
    } catch (e) {
      // Fallback if not JSON encoded
      const parsed = parseInt(sessionCompanyId);
      return parsed;
    }
  }
  // Fallback to localStorage
  const localCompanyId = localStorage.getItem('temp_com_id');
  if (localCompanyId) {
    return parseInt(localCompanyId);
  }
  // Default to 3 if nothing is set
  return 3;
};

// Template metadata cache to avoid repeated API calls
const templateMetadataCache = new Map();

// Widget component map for dynamic rendering
export const widgetComponents = {
  'text-label': SimpleTextLabel,
  'TextLabel': SimpleTextLabel,
  'text': TextLabel,       // Alias for text components
  'text-box': TextBox,
  'TextBox': TextBox,
  'button': Button,
  'Button': Button,
  'led-indicator': LEDIndicator,
  'LEDIndicator': LEDIndicator,
  'y_g': LEDIndicator,  // DynPage LED indicator
  'progress-bar': ProgressBar,
  'ProgressBar': ProgressBar,
  'gauge': Gauge,
  'Gauge': Gauge,
  'image': Image,
  'Image': Image,
  'toggle-switch': ToggleSwitch,
  'ToggleSwitch': ToggleSwitch,
  'trend-chart': TrendChart,
  'TrendChart': TrendChart,
  'combo-box': ComboBox,
  'ComboBox': ComboBox,
  'button-menu': ButtonMenu,
  'ButtonMenu': ButtonMenu,
  'set-value': SetValue,
  'SetValue': SetValue,
  'set-value-input': SetValueInput,
  'SetValueInput': SetValueInput,
  'selectbox': ButtonMenu,  // DynPage selectbox → ButtonMenu
  'box': Box,
  'Box': Box,
  'frame': Box,        // Frame is essentially a box (DynPage)
  'line': Line,
  'Line': Line,
  'circle': Circle,
  'Circle': Circle,
  'led-label': LEDLabel,
  'LEDLabel': LEDLabel,
  'label': LEDLabel,  // Alias for old RMMS "label" widget
  'popup': PopupWidget,        // DynPage Popup widget (lowercase)
  'popup-widget': PopupWidget,
  'PopupWidget': PopupWidget,
  'pop_up_widget': PopupWidget,  // Alias for old RMMS "pop_up_widget"
  'on/off': ToggleSwitch,      // DynPage on/off widget
  'tag': TextLabel,            // DynPage tag widget (displays tag value)
  // NOTE: 'selectbox' is mapped to ButtonMenu at line 82 - do not add duplicate here
  'alarm-list': null,
  'AlarmList': null,
  // VRF Room Widget
  'vrf-room': VRFRoomWidget,
  'VRFRoom': VRFRoomWidget,
  'VRFRoomWidget': VRFRoomWidget,
  'Popup': VRFRoomWidget,       // DynPage VRF Popup widget (uppercase - for VRF rooms)
  // VRF Floor Control Widget
  'vrf-floor-control': VRFFloorControl,
  'VRFFloorControl': VRFFloorControl,
};

// Custom Widget Component for composite widgets with variable binding support
const CustomWidget = ({ widget, width, height, customWidgetInputs = {}, tagValues = {}, previewMode = false, scaleChildren = true, parentScale = 1, ...props }) => {
  const [templateConfig, setTemplateConfig] = React.useState(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = React.useState(false);

  // Extract custom widget ID from widget type (e.g., "custom-3" -> 3)
  const customWidgetId = widget.customWidgetId || (widget.type?.match(/custom-(\d+)/)?.[1]);

  // Merge instance data with template data
  // Template takes precedence for metadata and canvas properties
  const mergedComponents = templateConfig?.components || widget.components || [];
  const mergedCanvasSize = templateConfig?.canvasSize || widget.customCanvasSize || { width: 300, height: 200 };

  // Calculate scale factors for child widget scaling
  // scaleChildren prop controls whether children should scale with container
  const shouldScale = scaleChildren !== false && (widget.scaleChildren !== false);
  const scaleX = shouldScale ? (width / mergedCanvasSize.width) : 1;
  const scaleY = shouldScale ? (height / mergedCanvasSize.height) : 1;
  const mergedCanvasProperties = templateConfig?.canvasProperties || widget.customCanvasProps || {};

  // Fetch template config on component mount
  React.useEffect(() => {
    if (customWidgetId && !isLoadingTemplate) {
      // Check cache first (now caching full config, not just metadata)
      if (templateMetadataCache.has(customWidgetId)) {
        const cachedConfig = templateMetadataCache.get(customWidgetId);
        setTemplateConfig(cachedConfig);
        return;
      }

      setIsLoadingTemplate(true);

      // Fetch template from API (include JWT token for auth)
      const token = sessionStorage.getItem('jwt_token');
      fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5169/api'}/SCADA/widgets/${customWidgetId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      })
        .then(response => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then(templateData => {
          try {
            const config = JSON.parse(templateData.widgetConfig || '{}');

            // Cache the full config (metadata, canvas properties, components)
            templateMetadataCache.set(customWidgetId, config);
            setTemplateConfig(config);
          } catch (error) {
            console.error('Failed to parse template config:', error);
          }
        })
        .catch(error => {
          console.error('Failed to fetch template config:', error);
        })
        .finally(() => {
          setIsLoadingTemplate(false);
        });
    }
  }, [customWidgetId, isLoadingTemplate]);

  // Merge instance metadata with template metadata
  // Template metadata takes precedence for componentActions to ensure latest bindings
  const mergedMetadata = {
    ...(widget.metadata || {}),
    ...(templateConfig?.metadata || {}),
    componentActions: {
      ...(widget.metadata?.componentActions || {}),
      ...(templateConfig?.metadata?.componentActions || {})
    }
  };

  const componentActions = mergedMetadata.componentActions || {};

  // Helper function to get default property for component type
  const getDefaultPropertyForComponent = (componentType) => {
    const typeMap = {
      'led-indicator': 'tagValue',
      'LEDIndicator': 'tagValue',
      'gauge': 'tagValue',
      'Gauge': 'tagValue',
      'progress-bar': 'tagValue',
      'ProgressBar': 'tagValue',
      'text-label': 'value',
      'TextLabel': 'value',
      'text-box': 'value',
      'TextBox': 'value',
      'button': 'value',
      'Button': 'value'
    };

    return typeMap[componentType] || 'tagValue';
  };

  // Helper function to resolve variable bindings like @inputs.I2
  const resolveBinding = (binding, componentId) => {
    if (!binding || typeof binding !== 'string') return binding;

    // Handle @inputs.variableName bindings
    if (binding.startsWith('@inputs.')) {
      const inputName = binding.replace('@inputs.', '');
      let inputValue = customWidgetInputs[inputName];

      // Helper to unwrap tag value objects ({ value, companyId, raw, unit } or { raw, unit, display })
      const unwrapTagValue = (tv, showUnit = false) => {
        if (tv && typeof tv === 'object') {
          if (tv.display !== undefined) {
            // var format: { raw, unit, formatted, display }
            if (showUnit && tv.unit) return `${tv.raw} ${tv.unit}`;
            return tv.raw;
          }
          if (tv.value !== undefined) {
            // tag format: { value, companyId, raw, unit }
            if (showUnit && tv.unit) return `${tv.value} ${tv.unit}`;
            return tv.value;
          }
        }
        return tv;
      };

      // Handle new {type: 'tag', id: 110, showUnit: true} format
      if (typeof inputValue === 'object' && inputValue !== null && inputValue.type && inputValue.id) {
        const showUnit = inputValue.showUnit || false;
        if (inputValue.type === 'tag' && tagValues) {
          const tagValue = tagValues[inputValue.id.toString()];
          if (tagValue !== undefined) {
            return unwrapTagValue(tagValue, showUnit);
          }
        }
        // Return just the id for workflow parameters
        return inputValue.id;
      }

      // If input value is a tag ID (string/number), resolve it to tag value (old format)
      if (inputValue !== undefined && inputValue !== null && tagValues && typeof inputValue !== 'boolean') {
        const tagValue = tagValues[inputValue.toString()];
        if (tagValue !== undefined) {
          return unwrapTagValue(tagValue);
        }
      }

      return inputValue !== undefined ? inputValue : binding;
    }

    // Handle @tags[tagId] bindings
    if (binding.match(/@tags\[([^\]]+)\]/)) {
      const match = binding.match(/@tags\[([^\]]+)\]/);
      const tagId = match[1].replace(/['"]/g, '');
      // Return the binding as-is for now, this should be handled by parent component
      return binding;
    }

    return binding;
  };

  // Helper function to resolve component properties with bindings
  const resolveComponentProps = (component) => {
    const props = { ...component.properties, ...component.props };
    const resolvedProps = {};

    // Only resolve bindings in preview mode
    if (!previewMode) {
      return props; // Return static properties in design mode
    }

    // Get component actions metadata if available (use merged metadata from parent scope)
    const componentAction = componentActions[component.id];


    // Handle flexible property bindings
    const appliedBindings = [];

    // Legacy valueBinding support (maps to default property)
    if (componentAction?.valueBinding) {
      const resolvedValue = resolveBinding(componentAction.valueBinding, component.id);

      // Get default property name for component type
      const defaultProperty = getDefaultPropertyForComponent(component.type);
      resolvedProps[defaultProperty] = resolvedValue;

      // When a tag input is bound, preserve tagId for all widget types (context menu, writable widgets)
      if (componentAction.valueBinding?.startsWith('@inputs.')) {
        const inputName = componentAction.valueBinding.replace('@inputs.', '');
        const inputValue = customWidgetInputs[inputName];
        let derivedTagId = null;
        if (typeof inputValue === 'object' && inputValue !== null && inputValue.type === 'tag' && inputValue.id) {
          derivedTagId = inputValue.id;
        } else if (typeof inputValue === 'string' || typeof inputValue === 'number') {
          derivedTagId = inputValue;
        }
        if (derivedTagId !== null) {
          resolvedProps.tagId = derivedTagId;
          appliedBindings.push({ propertyName: 'tagId', binding: componentAction.valueBinding, rawValue: derivedTagId, finalValue: derivedTagId, bindingType: 'derived' });
        }
      }

      appliedBindings.push({
        propertyName: defaultProperty,
        binding: componentAction.valueBinding,
        resolvedValue: resolvedValue,
        finalValue: resolvedValue
      });
    }

    // New propertyBindings support (maps any property to any binding)
    if (componentAction?.propertyBindings) {
      Object.entries(componentAction.propertyBindings).forEach(([propertyName, bindingConfig]) => {
        // Handle both old string format and new object format
        const binding = typeof bindingConfig === 'string' ? bindingConfig : bindingConfig?.source;

        if (binding) {
          const rawValue = resolveBinding(binding, component.id);
          const finalValue = applyConditionalMapping(rawValue, bindingConfig, propertyName);
          resolvedProps[propertyName] = finalValue;

          // When a tag input is bound, set tagId for all property types (context menu, writable widgets)
          if (binding.startsWith('@inputs.') && !resolvedProps.tagId) {
            const inputName = binding.replace('@inputs.', '');
            const inputValue = customWidgetInputs[inputName];
            let derivedTagId = null;
            if (typeof inputValue === 'object' && inputValue !== null && inputValue.type === 'tag' && inputValue.id) {
              derivedTagId = inputValue.id;
            } else if (typeof inputValue === 'string' || typeof inputValue === 'number') {
              derivedTagId = inputValue;
            }
            if (derivedTagId !== null) {
              resolvedProps.tagId = derivedTagId;
              // Mark tagId as applied so static props don't override it
              appliedBindings.push({ propertyName: 'tagId', binding: binding, rawValue: derivedTagId, finalValue: derivedTagId, bindingType: 'derived' });
            }
          }

          appliedBindings.push({
            propertyName: propertyName,
            binding: binding,
            rawValue: rawValue,
            finalValue: finalValue,
            bindingType: bindingConfig?.type || 'direct'
          });
        }
      });
    }

    if (appliedBindings.length > 0) {
      // Apply resolved bindings to resolvedProps
      appliedBindings.forEach(binding => {
        resolvedProps[binding.propertyName] = binding.finalValue;

      });
    }

    // Resolve other property bindings (but don't override already resolved ones!)
    Object.keys(props).forEach(key => {
      // Skip if this property was already set by propertyBindings
      if (!appliedBindings.some(binding => binding.propertyName === key)) {
        const value = props[key];
        resolvedProps[key] = resolveBinding(value, component.id);

        // Derive tagId from direct @inputs. references in component properties
        if (!resolvedProps.tagId && typeof value === 'string' && value.startsWith('@inputs.')) {
          const inputName = value.replace('@inputs.', '');
          const inputValue = customWidgetInputs[inputName];
          if (typeof inputValue === 'object' && inputValue !== null && inputValue.type === 'tag' && inputValue.id) {
            resolvedProps.tagId = inputValue.id;
          } else if (typeof inputValue === 'string' || typeof inputValue === 'number') {
            resolvedProps.tagId = inputValue;
          }
        }
      }
    });

    // Include appliedBindings for hasValueBinding check
    resolvedProps._appliedBindings = appliedBindings;
    return resolvedProps;
  };

  // Helper function to apply conditional mapping
  const applyConditionalMapping = (rawValue, bindingConfig, propertyName) => {
    if (!bindingConfig || typeof bindingConfig === 'string') {
      return rawValue; // Direct binding or old format
    }

    const { type, mappingTable } = bindingConfig;

    switch (type) {
      case 'boolean': {
        const boolValue = Boolean(rawValue);
        // FIX: mappingTable anahtarları "0" ve "1" formatında, "false"/"true" değil
        // Bu yüzden rawValue'yu direkt string'e çevirip kullanıyoruz
        let result = mappingTable[String(rawValue)] || rawValue;

        return result;
      }

      case 'range': {
        const numValue = Number(rawValue);
        if (isNaN(numValue)) return rawValue;

        // New format: ranges array from Widget Designer
        if (mappingTable.ranges && Array.isArray(mappingTable.ranges)) {
          for (const range of mappingTable.ranges) {
            if (numValue >= range.min && numValue <= range.max) {
              return range.output;
            }
          }
          return rawValue; // No range matched
        }

        // Legacy format: string keys (for backward compatibility)
        for (const [rangeKey, rangeValue] of Object.entries(mappingTable)) {
          if (rangeKey.startsWith('<')) {
            const threshold = Number(rangeKey.substring(1));
            if (numValue < threshold) return rangeValue;
          } else if (rangeKey.startsWith('>')) {
            const threshold = Number(rangeKey.substring(1));
            if (numValue > threshold) return rangeValue;
          } else if (rangeKey.includes('-')) {
            const [min, max] = rangeKey.split('-').map(Number);
            if (numValue >= min && numValue <= max) return rangeValue;
          }
        }
        return rawValue; // No range matched
      }

      case 'value': {
        return mappingTable[rawValue.toString()] || rawValue;
      }

      case 'direct':
      default:
        return rawValue;
    }
  };

  // Helper function to resolve canvas properties with bindings
  const resolveCanvasProperties = () => {
    const canvasBindings = mergedMetadata.canvasBindings || {};
    const resolvedCanvasProps = { ...mergedCanvasProperties };

    // Only resolve canvas bindings in preview mode
    if (!previewMode) {
      return resolvedCanvasProps; // Return static canvas properties in design mode
    }

    Object.entries(canvasBindings).forEach(([propertyName, bindingConfig]) => {
      // Handle both old string format and new object format
      const source = typeof bindingConfig === 'string' ? bindingConfig : bindingConfig?.source;

      if (source && source.trim()) {
        const rawValue = resolveBinding(source, 'canvas');
        const finalValue = applyConditionalMapping(rawValue, bindingConfig, propertyName);

        resolvedCanvasProps[propertyName] = finalValue;
      }
    });

    return resolvedCanvasProps;
  };

  const finalCanvasProperties = resolveCanvasProperties();

  return (
    <div
      style={{
        position: 'relative',
        width: width,
        height: height,
        border: finalCanvasProperties.borderWidth ? `${finalCanvasProperties.borderWidth}px solid ${finalCanvasProperties.borderColor || '#ddd'}` : 'none',
        borderRadius: `${finalCanvasProperties.borderRadius || 0}px`,
        backgroundColor: finalCanvasProperties.backgroundColor || widget.backgroundColor || 'transparent',
        opacity: finalCanvasProperties.opacity !== undefined ? finalCanvasProperties.opacity : 1,
        overflow: 'visible'
      }}
    >
      {mergedComponents.length === 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#666',
          fontSize: '12px',
          textAlign: 'center'
        }}>
          Custom Widget<br/>
          (No Components)
        </div>
      )}
      {mergedComponents.map((component, index) => {

        const ChildComponent = widgetComponents[component.type];

        if (!ChildComponent) {
          console.warn(`No component found for type: ${component.type}`);
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                left: component.x * scaleX,
                top: component.y * scaleY,
                width: component.width * scaleX,
                height: component.height * scaleY,
                backgroundColor: '#ffcccc',
                border: '1px dashed #ff0000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px'
              }}
            >
              Unknown: {component.type}
            </div>
          );
        }

        // Get component actions metadata to check for valueBinding
        const metadata = widget.metadata || {};
        const componentAction = componentActions[component.id];

        // If component has valueBinding, prioritize resolved props over static values
        let hasValueBinding = componentAction?.valueBinding;

        // Resolve component properties with variable bindings
        const resolvedProps = resolveComponentProps(component);

        // Update hasValueBinding if component has property bindings (appliedBindings is available in resolveComponentProps)
        const appliedBindingsFromResolve = resolvedProps._appliedBindings || [];
        delete resolvedProps._appliedBindings; // Clean up
        if (appliedBindingsFromResolve.length > 0) {
          hasValueBinding = true;
        }

        // console.log('🔍 After resolveComponentProps:', { ... });

        // Clean component properties to prevent overriding resolved values
        const cleanComponent = { ...component };
        delete cleanComponent.properties; // Remove static properties that might conflict
        delete cleanComponent.props; // Remove props that might conflict with resolved values

        // Get tag value for this component if it has a tagId
        const componentTagId = component.properties?.tagId || component.tagId;
        const componentTagValue = componentTagId ? tagValues[componentTagId] : null;

        // Get component tag info first
        // (text binding logic will be done after binding resolution)

        // Simple approach: Resolved properties should override static properties
        const finalProps = {
          ...component.properties,
          ...component.props,
          width: component.width,
          height: component.height,
          id: `${widget.id}_${component.id}`,
          // Add companyId from session/local storage
          companyId: getCompanyId(),
          // Add tag value if available
          ...(componentTagId && { tagValue: componentTagValue }),
          // Add parent widget text for ALARM/NORMAL detection
          parentText: widget.text || widget.properties?.text || widget.props?.text,
          // IMPORTANT: resolvedProps should come LAST to override static values
          ...resolvedProps
        };




        // Add onClick handler for components with actions
        const actionComponentAction = componentActions[component.id];

        // Enhanced props with action handling
        const enhancedProps = { ...finalProps };

        if (actionComponentAction?.onClick && previewMode) {
          enhancedProps.onClick = async () => {
            const actionConfig = actionComponentAction.onClick;

            try {
              // Handle workflow action
              if (actionConfig.actionType === 'workflow' && actionConfig.workflowName) {

                // Prepare input parameters from inputBindings
                const parameters = {};
                let tagId = null;

                if (actionConfig.inputBindings) {
                  Object.entries(actionConfig.inputBindings).forEach(([inputName, binding]) => {
                    // Source'u boş olan binding'leri işleme (I3 gibi)
                    if (!binding.source || binding.source === '') {
                      return;
                    }

                    // Sadece workflow'un gerçek inputlarını işle (detailsCache.inputs'ta olanlar)
                    const isWorkflowInput = actionConfig.detailsCache?.inputs?.some(
                      input => input.name === inputName
                    );

                    if (!isWorkflowInput) {
                      return;
                    }

                    let value = binding.customValue;

                    // Resolve tag values
                    if (binding.source && binding.source.startsWith('tags.')) {
                      const extractedTagId = binding.source.replace('tags.', '');
                      value = tagValues[extractedTagId];
                      parameters[`${inputName}_tagId`] = extractedTagId;
                      parameters[inputName] = value;
                      if (!tagId) tagId = extractedTagId;
                    }

                    // Resolve custom widget inputs
                    else if (binding.source && (binding.source.startsWith('@inputs.') || binding.source.startsWith('inputs.'))) {
                      const inputKey = binding.source.replace('@inputs.', '').replace('inputs.', '');
                      const inputValue = customWidgetInputs[inputKey];

                      // inputValue artık {type: 'tag', id: 110} formatında
                      if (typeof inputValue === 'object' && inputValue !== null) {
                        if (inputValue.type === 'tag') {
                          // Workflow parametresine _tagId ekle (IN1_tagId gibi)
                          parameters[`${inputName}_tagId`] = inputValue.id;
                          parameters[inputName] = inputValue.id;
                          if (!tagId) tagId = inputValue.id;
                        } else if (inputValue.type === 'var') {
                          // Workflow parametresine _varId ekle (IN1_varId gibi)
                          parameters[`${inputName}_varId`] = inputValue.id;
                          parameters[inputName] = inputValue.id;
                        }
                      } else if (inputValue !== undefined && inputValue !== null) {
                        parameters[inputName] = inputValue;
                      }
                    }
                  });
                }

                // Ana tagId'yi belirle: workflow parametrelerinden herhangi birinin tagId'si
                // (I1, I2 değil, IN1 gibi workflow parametreleri)
                if (!tagId && Object.keys(parameters).length > 0) {
                  // _tagId ile biten ilk parametreyi bul
                  const tagIdParam = Object.keys(parameters).find(key => key.endsWith('_tagId'));
                  if (tagIdParam) {
                    tagId = parameters[tagIdParam];
                  }
                }

                // tagId'yi parameters'a ekle
                if (tagId) {
                  parameters.tagId = parseInt(tagId);
                }

                // enableLogging'i parameters'a ekle
                parameters.enableLogging = true;

                // Call MetisTaskEngine workflow API with correct endpoint
                const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5169/api');
                const companyId = 1; // Default company ID

                // Build URL (enableLogging artık body'de)
                let url = `${API_BASE}/Task/workflows/execute-production/${companyId}/${actionConfig.workflowName}`;

                const response = await fetch(
                  url,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(parameters) // Send parameters directly as body
                  }
                );

                if (!response.ok) {
                  throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const result = await response.json();
              }

              // Handle code action
              if (actionConfig.actionType === 'code' && actionConfig.componentName) {
                // Code execution would be implemented here
              }

            } catch (error) {
              console.error('❌ Failed to execute component action:', error);

              // Show error notification
              if (window.showNotification) {
                window.showNotification(`Failed to execute action: ${error.message}`, 'error');
              } else {
                alert(`Failed to execute action: ${error.message}`);
              }
            }
          };
        }

        // Scale font and border properties to match designer behavior
        const minScale = Math.min(scaleX, scaleY);
        if (minScale !== 1) {
          if (enhancedProps.fontSize) {
            enhancedProps.fontSize = Math.round(enhancedProps.fontSize * minScale);
          }
          if (enhancedProps.borderWidth) {
            enhancedProps.borderWidth = Math.max(1, Math.round(enhancedProps.borderWidth * minScale));
          }
          if (enhancedProps.borderRadius) {
            enhancedProps.borderRadius = Math.round(enhancedProps.borderRadius * minScale);
          }
        }

        // Determine bound tag ID for context menu
        let boundTagId = componentTagId || null;

        if (!boundTagId && previewMode) {
          // 1. Check componentActions bindings
          const componentActionForCtx = componentActions[component.id];
          const bindings = [
            componentActionForCtx?.valueBinding,
            ...(componentActionForCtx?.propertyBindings
              ? Object.values(componentActionForCtx.propertyBindings).map(b => typeof b === 'string' ? b : b?.source)
              : [])
          ].filter(Boolean);

          for (const binding of bindings) {
            if (typeof binding === 'string' && binding.startsWith('@inputs.')) {
              const inputName = binding.replace('@inputs.', '');
              const inputVal = customWidgetInputs[inputName];
              if (inputVal && typeof inputVal === 'object' && inputVal.type === 'tag' && inputVal.id) {
                boundTagId = inputVal.id;
                break;
              } else if (inputVal && typeof inputVal !== 'object') {
                boundTagId = inputVal;
                break;
              }
            }
          }

          // 2. Scan direct @inputs. references in component properties
          if (!boundTagId) {
            const allProps = { ...component.properties, ...component.props };
            for (const val of Object.values(allProps)) {
              if (typeof val === 'string' && val.startsWith('@inputs.')) {
                const inputName = val.replace('@inputs.', '');
                const inputVal = customWidgetInputs[inputName];
                if (inputVal && typeof inputVal === 'object' && inputVal.type === 'tag' && inputVal.id) {
                  boundTagId = inputVal.id;
                  break;
                } else if (typeof inputVal === 'string' || typeof inputVal === 'number') {
                  boundTagId = inputVal;
                  break;
                }
              }
            }
          }
        }

        // Fallback: check if tagId was resolved through props (static or binding-derived)
        if (!boundTagId && previewMode) {
          const candidateTagId = enhancedProps.tagId || finalProps.tagId || null;
          if (typeof candidateTagId === 'number' || typeof candidateTagId === 'string') {
            boundTagId = candidateTagId;
          }
        }

        // Context menu handler for tag info
        const handleComponentContextMenu = boundTagId && previewMode ? (e) => {
          e.preventDefault();
          e.stopPropagation();
          const tagIdStr = String(boundTagId);
          const tagVal = tagValues[tagIdStr];
          const displayValue = tagVal && typeof tagVal === 'object' ? tagVal.value : tagVal;
          const tagName = (tagVal && typeof tagVal === 'object' && tagVal.name) || component.properties?.tagName || `Tag_${tagIdStr}`;
          window.dispatchEvent(new CustomEvent('openTagInfo', {
            detail: {
              tagId: boundTagId,
              tagName: tagName,
              currentValue: displayValue
            }
          }));
        } : undefined;

        // Apply scale factors to child widget dimensions
        const scaledWidth = component.width * scaleX;
        const scaledHeight = component.height * scaleY;

        // Normalize borderWidth to counteract parent CSS transform scale
        if (parentScale > 0 && parentScale !== 1) {
          const childBorderWidth = enhancedProps.borderWidth ?? component.properties?.borderWidth;
          if (childBorderWidth) {
            enhancedProps.borderWidth = childBorderWidth / parentScale;
          }
        }

        // Shape components (box, line, circle) without tag bindings should not block mouse events
        const isDecorativeShape = ['box', 'Box', 'line', 'Line', 'circle', 'Circle', 'frame'].includes(component.type) && !boundTagId;

        return (
          <div
            key={index}
            onContextMenuCapture={handleComponentContextMenu}
            style={{
              position: 'absolute',
              left: component.x * scaleX,
              top: component.y * scaleY,
              width: scaledWidth,
              height: scaledHeight,
              cursor: boundTagId && previewMode ? 'context-menu' : undefined,
              pointerEvents: isDecorativeShape ? 'none' : undefined
            }}
          >
            <ChildComponent {...enhancedProps} width={scaledWidth} height={scaledHeight} />
          </div>
        );
      })}
    </div>
  );
};


// Simple cache for custom widgets loaded from backend
let customWidgetsCache = null;

// Function to load custom widgets from backend
export const loadCustomWidgets = async () => {
  try {
    customWidgetsCache = await widgetService.getCustomWidgets(null, true);
    return customWidgetsCache;
  } catch (error) {
    customWidgetsCache = [];
    return [];
  }
};

// Function to get custom widget definition by name/type
export const getCustomWidgetDefinition = (widgetName) => {
  if (!customWidgetsCache) return null;

  // Extract ID if format is custom-{id}
  const customIdMatch = widgetName.match(/^custom-(\d+)$/);
  if (customIdMatch) {
    const widgetId = parseInt(customIdMatch[1]);
    const definition = customWidgetsCache.find(cw => cw.widgetId === widgetId);
    if (definition) {
      return definition;
    }
  }

  // Try direct match by name
  let definition = customWidgetsCache.find(cw =>
    cw.widgetType === 'custom' && (
      cw.widgetName === widgetName ||
      cw.widgetName === widgetName.replace('custom-', '') ||
      widgetName.includes(cw.widgetName.toLowerCase()) ||
      cw.widgetName.toLowerCase().includes(widgetName.replace('custom-', ''))
    )
  );

  // Special case mappings
  if (!definition && widgetName === 'custom-3') {
    definition = customWidgetsCache.find(cw =>
      cw.widgetName === 'KayıtSoru' || cw.widgetName.toLowerCase().includes('kayıt')
    );
  }

  if (!definition) {
    console.warn('Custom widget not found:', widgetName);
  }

  return definition;
};

// Helper function to render widget
export const renderWidget = (widget, tagValues = {}, previewMode = false, parentScale = 1) => {
  let componentType = widget.type || widget.component;

  // Handle custom widgets - they are composite widgets with multiple components
  if (componentType && componentType.startsWith('custom-')) {
    // Try to get custom definition - first by type, then by customWidgetId if available
    let customDefinition = getCustomWidgetDefinition(componentType);

    // If not found by type and we have customWidgetId, try direct lookup
    if (!customDefinition && widget.customWidgetId) {
      const typeWithId = `custom-${widget.customWidgetId}`;
      customDefinition = getCustomWidgetDefinition(typeWithId);
    }

    // If still not found but we have components directly on widget, use them
    if (!customDefinition && widget.components) {
      const containerWidth = widget.width || 120;
      const containerHeight = widget.height || 60;

      // Extract custom widget inputs from widget properties (fallback case)
      const customWidgetInputs = {};
      const metadata = widget.metadata || {};
      const inputs = metadata.inputs || [];

      inputs.forEach(input => {
        const inputValue = widget.properties?.[`input_${input.name}`]
                        || widget[`input_${input.name}`]
                        || widget.properties?.[input.name]
                        || widget[input.name];
        if (inputValue !== undefined) {
          customWidgetInputs[input.name] = inputValue;
        }
      });

      const resolvedWidget = {
        ...widget,
        components: widget.components,
        metadata: metadata
      };
      return <CustomWidget
        widget={resolvedWidget}
        width={containerWidth}
        height={containerHeight}
        customWidgetInputs={customWidgetInputs}
        tagValues={tagValues}
        previewMode={previewMode}
        scaleChildren={widget.scaleChildren !== false}
        parentScale={parentScale}
      />;
    }

    if (customDefinition && customDefinition.widgetConfig) {
      try {
        const config = JSON.parse(customDefinition.widgetConfig);
        if (config.components) {
          // Use widget's page placement size; fallback to template canvas size
          const containerWidth = widget.width || config.canvasSize?.width || 120;
          const containerHeight = widget.height || config.canvasSize?.height || 60;

          // Extract custom widget inputs from widget properties
          const customWidgetInputs = {};
          const metadata = config.metadata || {};
          const inputs = metadata.inputs || [];

          // Map input definitions to actual values from widget properties
          inputs.forEach(input => {
            // Try multiple ways to get the input value - check prefixed version first
            // Priority: input_I1 format (original binding config) > I1 format > top level
            const inputValue = widget.properties?.[`input_${input.name}`]
                            || widget[`input_${input.name}`]
                            || widget.properties?.[input.name]
                            || widget[input.name];

            if (inputValue !== undefined) {
              customWidgetInputs[input.name] = inputValue;
            }
          });

          const resolvedWidget = {
            ...widget,
            components: config.components,
            metadata: metadata
          };

          return <CustomWidget
            widget={resolvedWidget}
            width={containerWidth}
            height={containerHeight}
            customWidgetInputs={customWidgetInputs}
            tagValues={tagValues}
            previewMode={previewMode}
            scaleChildren={widget.scaleChildren !== false}
            parentScale={parentScale}
          />;
        }
      } catch (error) {
        console.error('Error parsing custom widget config:', error);
      }
    }

    // If no definition found, show placeholder with debug info
    return (
      <div style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#f0f0f0', border: '1px dashed #ccc',
        fontSize: '12px', color: '#666', flexDirection: 'column'
      }}>
        <div>Custom Widget: {componentType}</div>
        <div style={{ fontSize: '10px' }}>ID: {widget.customWidgetId || 'undefined'}</div>
        <div style={{ fontSize: '10px' }}>Cache loaded: {customWidgetsCache ? 'Yes' : 'No'}</div>
      </div>
    );
  }
  
  const Component = widgetComponents[componentType];

  if (!Component) {
    console.error(`Widget component not found for type: ${componentType}`, {
      availableTypes: Object.keys(widgetComponents),
      requestedType: componentType
    });
    return null;
  }

  // Merge all properties including style properties
  // Extract tagId (handle both old format and new {type, id} format)
  const extractTagId = (tagIdValue, widget) => {
    // First try direct tagId
    let tagId = tagIdValue;

    // Handle tagBinding format (from converter)
    if (!tagId && widget.properties?.tagBinding?.tagId) {
      tagId = widget.properties.tagBinding.tagId;
    }

    // Handle {type: 'tag', id: 110} format
    if (typeof tagId === 'object' && tagId !== null) {
      return tagId.id;
    }
    return tagId;
  };

  // DEBUG: Removed excessive logging

  const props = {
    ...(widget.properties || widget.props || {}),
    // 🔧 STATUS LABEL: Explicitly include status label props
    isStatusLabel: widget.properties?.isStatusLabel || widget.props?.isStatusLabel || widget.isStatusLabel,
    originalOptions: widget.properties?.originalOptions || widget.props?.originalOptions || widget.originalOptions,
    // Include tagId and tagValue from widget level (added by PreviewMode)
    tagId: extractTagId(widget.tagId || widget.properties?.tagId || widget.props?.tagId, widget),
    tagValue: widget.tagValue !== undefined ? widget.tagValue : (widget.properties?.tagValue !== undefined ? widget.properties.tagValue : widget.props?.tagValue),
    value: widget.value !== undefined ? widget.value : (widget.properties?.value !== undefined ? widget.properties.value : widget.props?.value),
    // ✨ Add companyId from session/local storage
    companyId: widget.companyId || widget.properties?.companyId || widget.props?.companyId || getCompanyId(),
    // Include style properties that might be at widget level
    backgroundColor: widget.backgroundColor || widget.properties?.backgroundColor || widget.props?.backgroundColor,
    foreColor: widget.foreColor || widget.properties?.foreColor || widget.props?.foreColor,
    borderWidth: (() => {
      const bw = widget.borderWidth || widget.properties?.borderWidth || widget.props?.borderWidth;
      return (bw && parentScale > 0 && parentScale !== 1) ? bw / parentScale : bw;
    })(),
    borderColor: widget.borderColor || widget.properties?.borderColor || widget.props?.borderColor,
    borderRadius: widget.borderRadius !== undefined ? widget.borderRadius : (widget.properties?.borderRadius !== undefined ? widget.properties.borderRadius : widget.props?.borderRadius),
    fontSize: widget.fontSize || widget.properties?.fontSize || widget.props?.fontSize,
    fontWeight: widget.fontWeight || widget.properties?.fontWeight || widget.props?.fontWeight,
    fontFamily: widget.fontFamily || widget.properties?.fontFamily || widget.props?.fontFamily,
    textAlign: widget.textAlign || widget.properties?.textAlign || widget.props?.textAlign,
    opacity: widget.opacity || widget.properties?.opacity || widget.props?.opacity,
    // Gauge-specific properties
    startAngle: widget.startAngle !== undefined ? widget.startAngle : (widget.properties?.startAngle !== undefined ? widget.properties.startAngle : widget.props?.startAngle),
    endAngle: widget.endAngle !== undefined ? widget.endAngle : (widget.properties?.endAngle !== undefined ? widget.properties.endAngle : widget.props?.endAngle),
    min: widget.min !== undefined ? widget.min : (widget.properties?.min !== undefined ? widget.properties.min : (widget.properties?.minValue !== undefined ? widget.properties.minValue : widget.props?.min)),
    max: widget.max !== undefined ? widget.max : (widget.properties?.max !== undefined ? widget.properties.max : (widget.properties?.maxValue !== undefined ? widget.properties.maxValue : widget.props?.max)),
    // Size properties for gauge rendering
    width: widget.width || widget.properties?.width || widget.props?.width,
    height: widget.height || widget.properties?.height || widget.props?.height,
    // Zone properties
    showZones: widget.showZones !== undefined ? widget.showZones : (widget.properties?.showZones !== undefined ? widget.properties.showZones : widget.props?.showZones),
    normalZoneEnd: widget.normalZoneEnd !== undefined ? widget.normalZoneEnd : (widget.properties?.normalZoneEnd !== undefined ? widget.properties.normalZoneEnd : widget.props?.normalZoneEnd),
    warningZoneEnd: widget.warningZoneEnd !== undefined ? widget.warningZoneEnd : (widget.properties?.warningZoneEnd !== undefined ? widget.properties.warningZoneEnd : widget.props?.warningZoneEnd),
    // Display properties
    unit: widget.unit || widget.properties?.unit || widget.props?.unit,
    numberFormat: widget.numberFormat || widget.properties?.numberFormat || widget.props?.numberFormat,
    title: widget.title || widget.properties?.title || widget.props?.title,
    // Button properties - properties/props take priority over top-level (inspector writes to properties)
    text: widget.properties?.text !== undefined && widget.properties?.text !== null ? widget.properties.text : (widget.props?.text !== undefined && widget.props?.text !== null ? widget.props.text : widget.text),
    // Button action properties
    actionType: widget.actionType || widget.properties?.actionType || widget.props?.actionType,
    targetTagId: widget.targetTagId || widget.properties?.targetTagId || widget.props?.targetTagId,
    tagOperation: widget.tagOperation || widget.properties?.tagOperation || widget.props?.tagOperation,
    tagSetValue: widget.tagSetValue || widget.properties?.tagSetValue || widget.props?.tagSetValue,
    tagIncrementValue: widget.tagIncrementValue !== undefined ? widget.tagIncrementValue : (widget.properties?.tagIncrementValue !== undefined ? widget.properties.tagIncrementValue : widget.props?.tagIncrementValue),
    navigateTo: widget.navigateTo || widget.properties?.navigateTo || widget.props?.navigateTo,
    openInNewTab: widget.openInNewTab !== undefined ? widget.openInNewTab : (widget.properties?.openInNewTab !== undefined ? widget.properties.openInNewTab : widget.props?.openInNewTab),
    taskCommand: widget.taskCommand || widget.properties?.taskCommand || widget.props?.taskCommand,
    isEditMode: false, // Preview mode - actions are enabled
    taskParameters: widget.taskParameters || widget.properties?.taskParameters || widget.props?.taskParameters,
    // Image properties
    src: widget.src || widget.properties?.src || widget.props?.src,
    alt: widget.alt || widget.properties?.alt || widget.props?.alt,
    fit: widget.fit || widget.properties?.fit || widget.props?.fit,
    // ToggleSwitch properties
    label: widget.properties?.label || widget.props?.label || widget.label,
    checked: widget.checked !== undefined ? widget.checked : (widget.properties?.checked !== undefined ? widget.properties.checked : widget.props?.checked),
    size: widget.size || widget.properties?.size || widget.props?.size,
    // TrendChart properties
    tagIds: widget.tagIds || widget.properties?.tagIds || widget.props?.tagIds || [],
    maxDataPoints: widget.maxDataPoints !== undefined ? widget.maxDataPoints : (widget.properties?.maxDataPoints !== undefined ? widget.properties.maxDataPoints : widget.props?.maxDataPoints),
    refreshInterval: widget.refreshInterval !== undefined ? widget.refreshInterval : (widget.properties?.refreshInterval !== undefined ? widget.properties.refreshInterval : widget.props?.refreshInterval),
    showGrid: widget.showGrid !== undefined ? widget.showGrid : (widget.properties?.showGrid !== undefined ? widget.properties.showGrid : widget.props?.showGrid),
    showLegend: widget.showLegend !== undefined ? widget.showLegend : (widget.properties?.showLegend !== undefined ? widget.properties.showLegend : widget.props?.showLegend),
    yAxisMin: widget.yAxisMin || widget.properties?.yAxisMin || widget.props?.yAxisMin,
    yAxisMax: widget.yAxisMax || widget.properties?.yAxisMax || widget.props?.yAxisMax,
    dataMode: widget.dataMode || widget.properties?.dataMode || widget.props?.dataMode,
    lineColors: widget.lineColors || widget.properties?.lineColors || widget.props?.lineColors,
    // ComboBox properties
    options: widget.options || widget.properties?.options || widget.props?.options || [],
    selectedValue: widget.selectedValue || widget.properties?.selectedValue || widget.props?.selectedValue,
    placeholder: widget.placeholder || widget.properties?.placeholder || widget.props?.placeholder,
    variant: widget.variant || widget.properties?.variant || widget.props?.variant,
    // ButtonMenu properties - tagValue is CRITICAL for ButtonMenu to display and respond to tag value changes
    colorMap: widget.colorMap || widget.properties?.colorMap || widget.props?.colorMap || {},
    textColor: widget.textColor || widget.properties?.textColor || widget.props?.textColor,
    defaultColor: widget.defaultColor || widget.properties?.defaultColor || widget.props?.defaultColor,
    // ButtonMenu options array for value selection
    options: widget.options || widget.properties?.options || widget.props?.options || [],
    // Status label properties (for text-label with colorMap format)
    textColorMap: widget.textColorMap || widget.properties?.textColorMap || widget.props?.textColorMap,
    valueTextMap: widget.valueTextMap || widget.properties?.valueTextMap || widget.props?.valueTextMap,
    // Shape properties (Box, Circle, Line)
    direction: widget.direction || widget.properties?.direction || widget.props?.direction,
    // VRF Room Widget properties
    elementDetails: widget.elementDetails || widget.properties?.elementDetails || widget.props?.elementDetails,
    tagId2: widget.tagId2 || widget.properties?.tagId2 || widget.elementDetails?.tagId2,
    tagId3: widget.tagId3 || widget.properties?.tagId3 || widget.elementDetails?.tagId3,
    tagId4: widget.tagId4 || widget.properties?.tagId4 || widget.elementDetails?.tagId4,
    tagId5: widget.tagId5 || widget.properties?.tagId5 || widget.elementDetails?.tagId5,
    tagId6: widget.tagId6 || widget.properties?.tagId6 || widget.elementDetails?.tagId6,
    tagId7: widget.tagId7 || widget.properties?.tagId7 || widget.elementDetails?.tagId7,
    tagId8: widget.tagId8 || widget.properties?.tagId8 || widget.elementDetails?.tagId8,
    tagId9: widget.tagId9 || widget.properties?.tagId9 || widget.elementDetails?.tagId9,
    tagId10: widget.tagId10 || widget.properties?.tagId10 || widget.elementDetails?.tagId10,
    tagId11: widget.tagId11 || widget.properties?.tagId11 || widget.elementDetails?.tagId11,
    tagId12: widget.tagId12 || widget.properties?.tagId12 || widget.elementDetails?.tagId12,
    tagId13: widget.tagId13 || widget.properties?.tagId13 || widget.elementDetails?.tagId13,
    tagId14: widget.tagId14 || widget.properties?.tagId14 || widget.elementDetails?.tagId14,
    tagId15: widget.tagId15 || widget.properties?.tagId15 || widget.elementDetails?.tagId15,
    tagId16: widget.tagId16 || widget.properties?.tagId16 || widget.elementDetails?.tagId16,
    value_list: widget.value_list || widget.properties?.value_list || widget.elementDetails?.value_list,
    border_list: widget.border_list || widget.properties?.border_list || widget.elementDetails?.border_list,
    bg_list: widget.bg_list || widget.properties?.bg_list || widget.elementDetails?.bg_list,
    font_list: widget.font_list || widget.properties?.font_list || widget.elementDetails?.font_list,
    value_list2: widget.value_list2 || widget.properties?.value_list2 || widget.elementDetails?.value_list2,
    border_list2: widget.border_list2 || widget.properties?.border_list2 || widget.elementDetails?.border_list2,
    bg_list2: widget.bg_list2 || widget.properties?.bg_list2 || widget.elementDetails?.bg_list2,
    bgColor: widget.bgColor || widget.properties?.bgColor || widget.backgroundColor,
    bgColor2: widget.bgColor2 || widget.properties?.bgColor2,
    fontColor: widget.fontColor || widget.properties?.fontColor || widget.foreColor,
    index: widget.index || widget.properties?.index,
    index2: widget.index2 || widget.properties?.index2,
    group_t_id: widget.group_t_id || widget.properties?.group_t_id,
    // Pass tagValues for VRF widget
    tagValues: tagValues
  };

  // DEBUG: Removed excessive logging

  return (
    <Component
      {...props}
      width={widget.width}
      height={widget.height}
    />
  );
};

// Cache utility functions for template metadata
export const clearTemplateMetadataCache = (widgetId = null) => {
  if (widgetId) {
    templateMetadataCache.delete(widgetId);
  } else {
    templateMetadataCache.clear();
  }
};

export const getTemplateMetadataCache = () => templateMetadataCache;

export default widgetComponents;